/**
 * 金額格式化與單位轉換工具
 * 純呈現功能，不包含業務邏輯
 *
 * SSOT（單一真相來源）：
 * - 資料庫與 API 一律使用「分」（cents）儲存與傳輸（訂單、支付、商品價等 *_twd）
 * - UI 顯示時使用 formatTwdFromCents(cents)；輸入「元」送 API 前使用 yuanToCents(yuan)
 * - 例外：Lite 活動 productList.price、點數等值金額 points_yuan_equivalent 為「元」，顯示用 formatMoney(yuan)
 *
 * 禁止：前端不得將 API 的 *_twd / 金額欄位直接 toLocaleString() 或當成「元」顯示，
 * 一律經 formatTwdFromCents（分→元）或 formatMoney（已為元）再顯示。
 *
 * 注意：計算邏輯已移至 @pos/shared/lib/pricing
 * 此檔案只保留格式化與單位轉換函數
 */
/**
 * 分 → 元（用於顯示或計算）
 *
 * @param cents 金額（分）
 * @returns 金額（元）
 */
export function centsToYuan(cents) {
    return Number.isFinite(Number(cents)) ? Number(cents) / 100 : 0;
}
/**
 * 元 → 分（用於寫入 DB / 送 API，例如後台輸入「元」後轉成「分」）
 *
 * @param yuan 金額（元）
 * @returns 金額（分）
 */
export function yuanToCents(yuan) {
    return Number.isFinite(Number(yuan)) ? Math.round(Number(yuan) * 100) : 0;
}
/**
 * 將 API/DB 的「分」格式化成台幣顯示字串
 * 全專案價格顯示請統一使用此函數，確保 SSOT（API 一律為分）
 *
 * @param cents 金額（分）
 * @returns 例如 "NT$1,000"
 */
export function formatTwdFromCents(cents) {
    return formatMoney(centsToYuan(cents ?? 0));
}
/**
 * 格式化金額顯示（台灣格式）
 * 輸入為「元」；若資料來源為 API（分）請改用 formatTwdFromCents
 *
 * @param amount 金額（元，數字）
 * @returns 格式化後的字串，例如："NT$1,000"
 *
 * @example
 * ```ts
 * formatMoney(1000) // "NT$1,000"
 * formatMoney(120)  // "NT$120"
 * formatTwdFromCents(29900) // "NT$299"
 * ```
 */
export function formatMoney(amount) {
    return new Intl.NumberFormat('zh-TW', {
        style: 'currency',
        currency: 'TWD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(amount);
}
/**
 * 格式化金額顯示（含小數）
 *
 * @param amount 金額（數字）
 * @returns 格式化後的字串，例如："NT$1,000.00"
 *
 * @example
 * ```ts
 * formatMoneyWithDecimals(1000) // "NT$1,000.00"
 * formatMoneyWithDecimals(120.5) // "NT$120.50"
 * ```
 */
export function formatMoneyWithDecimals(amount) {
    return new Intl.NumberFormat('zh-TW', {
        style: 'currency',
        currency: 'TWD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
}
export function formatCurrency(amount, options = {}) {
    const { showDecimals = false, currency = 'TWD', locale = 'zh-TW' } = options;
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: showDecimals ? 2 : 0,
        maximumFractionDigits: showDecimals ? 2 : 0,
    }).format(amount);
}
// ==================== 向後相容性：保留舊的計算函數 ====================
// 這些函數會調用新的 pricing 模組，確保向後相容
// 建議新代碼直接使用 @pos/shared/lib/pricing
import { calculateDiscountAmount as calcDiscount, calculateTaxAmount as calcTax, calculateDiscountedAmount as calcDiscounted, calculateChange as calcChange, calculateOrderItemTotal as calcItemTotal, calculateTotal as calcTotal, roundMoneyToDecimals, toInteger, toNumber, } from '../lib/pricing/index.js';
/**
 * @deprecated 請使用 @pos/shared/lib/pricing 中的 calculateDiscountAmount
 * 此函數保留僅為向後相容
 */
export function calculateDiscountAmount(originalPrice, discountType, discountValue) {
    const result = calcDiscount(originalPrice, discountType, discountValue);
    return toInteger(result);
}
/**
 * @deprecated 請使用 @pos/shared/lib/pricing 中的 calculateTaxAmount
 * 此函數保留僅為向後相容
 */
export function calculateTaxAmount(subtotal, taxRateBps) {
    const result = calcTax(subtotal, taxRateBps);
    return toInteger(result);
}
/**
 * @deprecated 請使用 @pos/shared/lib/pricing 中的 calculateDiscountedAmount
 * 此函數保留僅為向後相容
 */
export function calculateDiscountedAmount(originalAmount, discountAmount) {
    const result = calcDiscounted(originalAmount, discountAmount);
    return toInteger(result);
}
/**
 * @deprecated 請使用 @pos/shared/lib/pricing 中的 calculateChange
 * 此函數保留僅為向後相容
 */
export function calculateChange(paidAmount, totalAmount) {
    const result = calcChange(paidAmount, totalAmount);
    return toInteger(result);
}
/**
 * @deprecated 請使用 @pos/shared/lib/pricing 中的 calculateOrderItemTotal
 * 此函數保留僅為向後相容
 */
export function calculateOrderItemTotal(quantity, finalPrice, taxAmount) {
    const result = calcItemTotal(quantity, finalPrice, taxAmount);
    return toInteger(result);
}
/**
 * @deprecated 請使用 @pos/shared/lib/pricing 中的 calculateTotal
 * 此函數保留僅為向後相容
 */
export function calculateTotal(items) {
    const result = calcTotal(items);
    return toInteger(result);
}
/**
 * @deprecated 請使用 @pos/shared/lib/pricing 中的 roundMoneyToDecimals
 * 此函數保留僅為向後相容
 */
export function roundToDecimals(value, decimals = 2) {
    const result = roundMoneyToDecimals(value, decimals);
    return toNumber(result);
}
